import { Button, Modal, Stack, Text } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ChangePasswordForm } from "@/components/settings/ChangePasswordForm";
import { setActiveCompany } from "@/lib/api-client";
import { logout } from "@/lib/auth";
import { queryKeys } from "@/lib/query-keys";
import { useUserStore } from "@/store/user-store";

/**
 * 비밀번호 만료 하드게이트 (전역 보안정책 · FR-PLT).
 *
 * /me passwordExpired=true 이면 앱 위에 닫을 수 없는 차단 모달을 띄워 비밀번호
 * 변경을 강제한다(변경 전 앱 사용 불가). 라우팅을 전혀 건드리지 않는 오버레이
 * 방식이라 리다이렉트 루프가 원천적으로 발생하지 않음.
 *
 * - 변경 성공 → /me 무효화 → passwordExpired 해제 → 모달 자동 소멸.
 * - 만료는 인증을 막지 않으므로 현재(만료된) 비밀번호로 변경이 가능(치킨-에그 없음).
 * - 현재 비번을 잊은 경우를 위해 로그아웃 탈출구만 열어 둠.
 * - 하단 줄 `[로그아웃] · 빈 공간 · [변경하고 계속]`(규칙 ui-button-placement §1). 닫을 수 없는 창이라 X·취소 없음(예외).
 *   셸은 Mantine Modal 유지 — 포커스 가두기(trapFocus)로 뒤쪽 앱 조작을 막는 차단 게이트이기 때문.
 */
export function PasswordExpiredGate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useUserStore((s) => s.user);
  const clearUser = useUserStore((s) => s.clearUser);

  const expired = user?.passwordExpired ?? false;

  async function onLogout() {
    // AppShell 사용자 메뉴 로그아웃과 동일 절차(스토어·활성회사·캐시 정리 후 로그인).
    clearUser();
    setActiveCompany(null);
    queryClient.clear();
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <Modal
      opened={expired}
      onClose={() => {
        /* 차단 모달 — 닫기 불가(변경 완료 시에만 소멸) */
      }}
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      centered
      trapFocus
      lockScroll
      title={t("security.passwordExpiredTitle", "비밀번호 변경 필요")}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t(
            "security.passwordExpiredGate",
            "전역 보안정책에 따라 비밀번호 사용 기간이 만료되었습니다. 계속하려면 새 비밀번호로 변경해 주세요.",
          )}
        </Text>
        <ChangePasswordForm
          submitLabel={t("security.passwordExpiredSubmit", "변경하고 계속")}
          secondaryAction={
            <Button variant="default" type="button" onClick={() => void onLogout()}>
              {t("user.logout", "로그아웃")}
            </Button>
          }
          onSuccess={() => {
            // /me 재조회 → 서버가 password_changed_at=now() 기준으로 만료 해제 → 게이트 소멸.
            void queryClient.invalidateQueries({ queryKey: queryKeys.me.current() });
          }}
        />
      </Stack>
    </Modal>
  );
}
